import { useEffect, useState } from 'react';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import type { TrackPermissionRow, TrackRole } from '@verybigthings/trackframe-shared/schemas';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { hub } from '@/lib/hub-client';
import { splitPermissions } from './RolesMatrix';

export const ROLE_NAME_TAKEN_TEXT = 'A role with this name exists.';

/** Body for POST/PATCH — `description` is optional on the wire, never null. */
export function roleWriteBody(role: Pick<TrackRole, 'name' | 'description' | 'permissions'>) {
  const description = role.description?.trim();
  return {
    name: role.name.trim(),
    ...(description ? { description } : {}),
    permissions: role.permissions,
  };
}

function roleErrorMessage(err: unknown): string {
  if (err instanceof HubClientError && err.code === 'role_name_taken') return ROLE_NAME_TAKEN_TEXT;
  return (err as Error).message ?? 'Save failed';
}

function PermissionChecks({
  heading,
  rows,
  selected,
  onToggle,
}: {
  heading: string;
  rows: TrackPermissionRow[];
  selected: Set<string>;
  onToggle: (name: string, checked: boolean) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{heading}</p>
      {rows.map((row) => (
        <label key={row.name} className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={selected.has(row.name)}
            onCheckedChange={(checked) => onToggle(row.name, checked)}
            aria-label={row.name}
          />
          <span>{row.label}</span>
          {row.toolKind ? (
            <Badge variant={row.toolKind === 'write' ? 'default' : 'outline'}>{row.toolKind}</Badge>
          ) : (
            <span className="font-mono text-xs text-muted-foreground">{row.name}</span>
          )}
        </label>
      ))}
    </div>
  );
}

export interface RoleFormDialogProps {
  open: boolean;
  trackId: string;
  /** Present = edit (rename + full tick replacement); absent = create. */
  role?: TrackRole | null;
  permissions: TrackPermissionRow[];
  onClose: () => void;
  onSaved: (role: TrackRole) => void;
}

export function RoleFormDialog({ open, trackId, role, permissions, onClose, onSaved }: RoleFormDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(role?.name ?? '');
    setDescription(role?.description ?? '');
    setSelected(new Set(role?.permissions ?? []));
    setError(null);
  }, [open, role]);

  const groups = splitPermissions(permissions);
  const toggle = (permission: string, checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(permission);
      else next.delete(permission);
      return next;
    });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const body = roleWriteBody({ name, description, permissions: [...selected] });
    try {
      const saved = role
        ? await hub.tracksRoleUpdate({ params: { id: trackId, roleId: role.id }, body })
        : await hub.tracksRoleCreate({ params: { id: trackId }, body });
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(roleErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={role ? 'Edit role' : 'Add role'}
      description="Name the role, then tick what it may do. You can change ticks later in the matrix."
      className="max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="track-role-form" disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : role ? 'Save' : 'Create role'}
          </Button>
        </>
      }
    >
      <form id="track-role-form" className="space-y-4" onSubmit={submit}>
        <div className="space-y-2">
          <Label htmlFor="role-name">Name</Label>
          <Input
            id="role-name"
            required
            maxLength={64}
            autoComplete="off"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="role-description">Description</Label>
          <Input
            id="role-description"
            maxLength={512}
            autoComplete="off"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="max-h-72 space-y-3 overflow-y-auto">
          <PermissionChecks heading="App permissions" rows={groups.custom} selected={selected} onToggle={toggle} />
          <PermissionChecks heading="Copilot tools" rows={groups.tools} selected={selected} onToggle={toggle} />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </form>
    </Dialog>
  );
}
